import { useEffect, useRef, useState } from "react";

/**
 * CountUp — flight-board style number animator.
 *
 * Starts counting only once the element scrolls into view (single-shot, like an
 * airport split-flap coming online). Uses `requestAnimationFrame` driven by a
 * smooth `easeOutCubic` curve so the final digits settle gracefully rather than
 * ticking up linearly.
 *
 * Props:
 *   value:    the final numeric value
 *   duration: ms, default 1600
 *   prefix:   e.g. "₹" or ""
 *   suffix:   e.g. "+" or "★"
 *   locale:   Intl.NumberFormat locale, default "en-IN"
 *   decimals: integer, default 0
 */
export default function CountUp({
  value = 0,
  duration = 1600,
  prefix = "",
  suffix = "",
  locale = "en-IN",
  decimals = 0,
  className = "",
}) {
  const [display, setDisplay] = useState(0);
  const nodeRef = useRef(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return undefined;
    const target = Number(value) || 0;
    // Reset the latch so a value change post-mount (e.g. the review count
    // arriving from an API fetch after first render) re-runs the animation
    // from the current display to the new target instead of staying frozen.
    hasRunRef.current = false;
    const startAnim = () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      const start = performance.now();
      const from = display;
      const delta = target - from;
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        setDisplay(from + delta * easeOutCubic(t));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) startAnim(); });
    }, { threshold: 0.3 });
    obs.observe(node);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(display);

  return (
    <span ref={nodeRef} className={className} data-testid="countup">
      {prefix}{formatted}{suffix}
    </span>
  );
}
